/**
 * TaskStatusPhaseDialog - Confirmação de concluir/cancelar tarefa com ajuste de fase
 *
 * Exibido quando o usuário clica em "Concluir" ou "Cancelar" no modal de detalhes.
 * Permite (opcionalmente) ajustar a CATEGORIA e a FASE da tarefa no MESMO momento
 * da mudança de status — a RPC change_task_status (v1.8.0) executa todas as
 * operações na mesma transação, garantindo rollback total em caso de falha.
 *
 * @version 1.3.0
* RELEASE: 2026-10-11 v1.3.1 - isServiceOrder força categorias/fases da O.S. mesmo fora da rota de O.S.
 * RELEASE: 2026-10-09 v1.3.0 - Reuso pela Ordem de Serviço (`isServiceOrder`): catálogo de
 *          categorias/fases da OS, filtro `canMoveToPhase` e motivo obrigatório (`requireNote`).
 * RELEASE: 2026-08-22 v1.2.0 - Campo "Categoria" (permite concluir mudando de
 *          categoria + fase) e layout padronizado com PremiumDialogHeader
 *          (mesmo padrão do modal "Nova anotação" do Chat).
 * RELEASE: 2026-08-22 v1.1.0 - Motivo e descrição de conclusão (Retenção/Jurídico)
 * RELEASE: 2026-08-10 v1.0.0 - Criação inicial
 */
import { useEffect, useMemo, useState } from 'react';
import { useTaskCategories } from '@/hooks/useTaskCategories';
import { useTaskPhases } from '@/hooks/useTaskPhases';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { PremiumDialogHeader } from '@/components/shared/PremiumDialogHeader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { useTaskCompletionReasons } from '@/hooks/useTaskCompletionReasons';
import type { TaskModuleType } from '@/hooks/useTaskModuleType';

interface TaskStatusPhaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ação sendo confirmada */
  action: 'completed' | 'cancelled' | null;
  /** Título da tarefa (exibição) */
  taskTitle?: string;
  /** Categoria atual da tarefa */
  categoryId?: string | null;
  /** Fase atual da tarefa */
  currentPhaseId?: string | null;
  /** v1.1.0 — módulo da tarefa: define se o motivo de conclusão é exigido */
  taskType?: TaskModuleType;
  /**
   * Confirmação. v1.1.0: além da fase, envia motivo e descrição da conclusão
   * (apenas para Retenção/Jurídico ao concluir).
   */
  onConfirm: (payload: {
    phaseId: string | null;
    completionReasonId: string | null;
    completionNote: string | null;
  }) => Promise<void> | void;
  isSubmitting?: boolean;
  /** v1.3.0 — Ordem de Serviço: usa catálogo da OS (rota) e textos de OS */
  isServiceOrder?: boolean;
  /** v1.3.0 — filtra as fases que o perfil pode mover */
  canMoveToPhase?: (phaseId: string) => boolean;
  /** v1.3.0 — exige observação (ex.: motivo do cancelamento da OS) */
  requireNote?: boolean;
}

export function TaskStatusPhaseDialog({
  open,
  onOpenChange,
  action,
  taskTitle,
  categoryId,
  currentPhaseId,
  taskType = 'normal',
  onConfirm,
  isSubmitting = false,
  isServiceOrder = false,
  canMoveToPhase,
  requireNote = false,
}: TaskStatusPhaseDialogProps) {
  // v1.3.0 — OS não força módulo de Tarefas: hooks resolvem o catálogo da OS pela rota
  const moduleOverride = isServiceOrder ? undefined : taskType;
  const isComplete = action === 'completed';

  // v1.2.0 — categoria selecionada (permite mudar de categoria ao concluir)
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>(undefined);
  const { categories, isLoading: isLoadingCategories } = useTaskCategories(true, moduleOverride, !!isServiceOrder);

  // Fases da categoria selecionada (escopadas ao módulo da tarefa)
  const { phases: allPhases, isLoading } = useTaskPhases(selectedCategoryId, moduleOverride, !!isServiceOrder);
  // v1.3.0 — somente fases permitidas pelo perfil (a atual sempre aparece)
  const phases = useMemo(
    () => (canMoveToPhase ? allPhases.filter((p) => p.id === currentPhaseId || canMoveToPhase(p.id)) : allPhases),
    [allPhases, canMoveToPhase, currentPhaseId],
  );
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);

  // v1.1.0 — motivo de conclusão: obrigatório apenas em Retenção/Jurídico ao concluir
  const requiresReason =
    !isServiceOrder && isComplete && (taskType === 'retention' || taskType === 'legal');
  const { reasons, isLoading: isLoadingReasons } = useTaskCompletionReasons(
    taskType,
    true, // apenas motivos ativos
    requiresReason && open,
  );
  const [reasonId, setReasonId] = useState<string | null>(null);
  const [completionNote, setCompletionNote] = useState('');

  /**
   * Pré-seleção: fase final da categoria (quando existir), senão a fase atual.
   * Recalculada sempre que o dialog abre ou as fases carregam.
   */
  const suggestedPhaseId = useMemo(() => {
    if (!isComplete) return currentPhaseId ?? null;
    const finalPhase = phases.find((p) => p.is_final);
    return finalPhase?.id ?? currentPhaseId ?? null;
  }, [isComplete, phases, currentPhaseId]);

  useEffect(() => {
    if (open) setSelectedPhaseId(suggestedPhaseId);
  }, [open, suggestedPhaseId]);

  // Reset ao abrir: categoria atual, motivo/descrição vazios
  useEffect(() => {
    if (open) {
      setSelectedCategoryId(categoryId ?? undefined);
      setReasonId(null);
      setCompletionNote('');
    }
  }, [open, categoryId]);

  // Só envia a fase quando realmente mudou (evita update desnecessário)
  const handleConfirm = async () => {
    const phaseToSend =
      selectedPhaseId && selectedPhaseId !== currentPhaseId ? selectedPhaseId : null;
    await onConfirm({
      phaseId: phaseToSend,
      completionReasonId: requiresReason ? reasonId : null,
      // Limite defensivo — a RPC também valida o tamanho
      completionNote: requiresReason || requireNote ? completionNote.trim().slice(0, 1000) || null : null,
    });
  };

  // Bloqueia o confirmar enquanto o motivo obrigatório não for escolhido
  const isConfirmDisabled =
    isSubmitting || (requiresReason && !reasonId) || (requireNote && !completionNote.trim());
  const entity = isServiceOrder ? 'OS' : 'tarefa';

  return (
    <Dialog open={open} onOpenChange={(v) => !isSubmitting && onOpenChange(v)}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Cabeçalho padrão do sistema (mesmo do modal "Nova anotação") */}
        <PremiumDialogHeader
          icon={isComplete ? CheckCircle2 : XCircle}
          title={isComplete ? (isServiceOrder ? 'Revisar e concluir OS' : 'Concluir tarefa') : `Cancelar ${entity}`}
          description={
            taskTitle
              ? taskTitle
              : isComplete
                ? `Confirme a conclusão da ${entity}.`
                : `Confirme o cancelamento da ${entity}.`
          }
        />

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* v1.1.0 — Motivo da conclusão (Retenção/Jurídico) */}
          {requiresReason && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Motivo da conclusão *</Label>
                <Select
                  value={reasonId ?? undefined}
                  onValueChange={(v) => setReasonId(v)}
                  disabled={isLoadingReasons || isSubmitting || reasons.length === 0}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue
                      placeholder={
                        isLoadingReasons
                          ? 'Carregando motivos...'
                          : reasons.length === 0
                            ? 'Nenhum motivo cadastrado'
                            : 'Selecione o motivo'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {reasons.map((reason) => (
                      <SelectItem key={reason.id} value={reason.id}>
                        <span className="flex items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: reason.color || 'hsl(var(--muted-foreground))' }}
                          />
                          <span>{reason.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {reasons.length === 0 && !isLoadingReasons && (
                  <p className="text-[11px] text-destructive">
                    Cadastre os motivos em Configurações do módulo para poder concluir.
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Descrição (opcional)</Label>
                <Textarea
                  rows={3}
                  maxLength={1000}
                  value={completionNote}
                  onChange={(e) => setCompletionNote(e.target.value)}
                  placeholder="Detalhe o desfecho desta conclusão"
                  disabled={isSubmitting}
                />
              </div>
            </>
          )}

          {/* v1.3.0 — Observação obrigatória (cancelamento da OS) */}
          {requireNote && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Motivo do cancelamento *</Label>
              <Textarea rows={3} maxLength={1000} value={completionNote}
                onChange={(e) => setCompletionNote(e.target.value)}
                placeholder="Digite a observação obrigatória..." disabled={isSubmitting} />
              <p className="text-right text-[10px] text-muted-foreground">{completionNote.length}/1000</p>
            </div>
          )}

          {/* v1.2.0 — Categoria (opcional): permite mover a tarefa de categoria */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Categoria (opcional)</Label>
            <Select
              value={selectedCategoryId}
              onValueChange={(v) => {
                setSelectedCategoryId(v);
                // fase precisa ser reescolhida dentro da nova categoria
                setSelectedPhaseId(null);
              }}
              disabled={isLoadingCategories || isSubmitting}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Manter categoria atual" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    <span className="flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: cat.color || 'hsl(var(--muted-foreground))' }}
                      />
                      <span>{cat.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Fase */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{isServiceOrder ? 'Fase da OS (opcional)' : 'Fase da tarefa (opcional)'}</Label>
            <Select
              value={selectedPhaseId ?? undefined}
              onValueChange={(v) => setSelectedPhaseId(v)}
              disabled={isLoading || isSubmitting || phases.length === 0}
            >
              <SelectTrigger className="h-9">
                <SelectValue
                  placeholder={isLoading ? 'Carregando fases...' : 'Manter fase atual'}
                />
              </SelectTrigger>
              <SelectContent>
                {phases.map((phase) => (
                  <SelectItem key={phase.id} value={phase.id}>
                    <span className="flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: phase.color || 'hsl(var(--muted-foreground))' }}
                      />
                      <span>{phase.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
              A mudança de status, categoria e fase é aplicada na mesma operação. Se algo
              falhar, nada é alterado.
            </p>
          </div>
        </div>

        <DialogFooter className="border-t bg-muted/30 px-6 py-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Voltar
          </Button>
          <Button onClick={handleConfirm} disabled={isConfirmDisabled}>
            {isSubmitting && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
